import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface ParamDto { code: string; description: string; kind: "Numeric" | "Text" | "DateValue" | "EnumValue";
  int: number | null; string: string | null; dateTime: string | null; }

export function SystemParametersPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["system-parameters"], queryFn: () => apiFetch<ParamDto[]>("/api/system-parameters") });
  const [selected, setSelected] = useState<string | null>(null);
  const current = data?.find((p) => p.code === selected) ?? null;

  const patch = useMutation({
    mutationFn: ({ code, body }: { code: string; body: { int?: number | null; string?: string | null; dateTime?: string | null } }) =>
      apiFetch(`/api/system-parameters/${encodeURIComponent(code)}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["system-parameters"] }); toast.success("Saved"); },
  });
  const reset = useMutation({
    mutationFn: (code: string) => apiFetch(`/api/system-parameters/${encodeURIComponent(code)}/reset`, { method: "POST" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["system-parameters"] }); toast.success("Reset to default"); },
  });

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  return (
    <PageContainer>
      {portalNode && createPortal(
        <Link to="/admin" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[280px_1fr]">
        <aside className="space-y-1">
          <h2 className="text-lg font-medium">Parameters</h2>
          <ul className="space-y-1">
            {(data ?? []).map((p) => (
              <li key={p.code}>
                <button className={"w-full rounded px-2 py-1 text-left hover:bg-accent " + (p.code === selected ? "bg-accent" : "")} onClick={() => setSelected(p.code)}>
                  <div className="text-sm font-medium">{p.code}</div>
                  <div className="text-xs text-muted-foreground">{p.kind}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section>
          {current ? (
            <ParameterEditor key={current.code} param={current}
              onSave={(body) => patch.mutate({ code: current.code, body })}
              onReset={() => reset.mutate(current.code)} />
          ) : <p className="text-muted-foreground">Select a parameter on the left to edit.</p>}
        </section>
      </div>
    </PageContainer>
  );
}

function ParameterEditor({ param, onSave, onReset }: { param: ParamDto; onSave: (b: { int?: number | null; string?: string | null; dateTime?: string | null }) => void; onReset: () => void }) {
  const [intVal, setInt] = useState(param.int ?? 0);
  const [strVal, setStr] = useState(param.string ?? "");
  const [dateVal, setDate] = useState(param.dateTime?.slice(0, 10) ?? "");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (param.kind === "Numeric") onSave({ int: intVal });
    else if (param.kind === "DateValue") onSave({ dateTime: dateVal ? new Date(dateVal).toISOString() : null });
    else onSave({ string: strVal });
  }

  return (
    <form onSubmit={submit} className="max-w-xl space-y-3">
      <h2 className="text-xl font-medium">{param.code}</h2>
      <p className="text-sm text-muted-foreground">{param.description}</p>
      {param.kind === "Numeric" && <div><Label htmlFor="v">Integer value</Label><Input id="v" type="number" value={intVal} onChange={(e) => setInt(Number(e.target.value))} /></div>}
      {param.kind === "DateValue" && <div><Label htmlFor="v">Date</Label><Input id="v" type="date" value={dateVal} onChange={(e) => setDate(e.target.value)} /></div>}
      {(param.kind === "Text" || param.kind === "EnumValue") && <div><Label htmlFor="v">Value</Label><Input id="v" value={strVal} onChange={(e) => setStr(e.target.value)} /></div>}
      <div className="flex gap-2">
        <Button type="submit">Save</Button>
        <Button type="button" variant="outline" onClick={onReset}>Reset to seed default</Button>
      </div>
    </form>
  );
}
